import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import FormDialog from '../../../components/ui/FormDialog'
import FormTextField from '../../../components/ui/form/FormTextField'
import { useZodForm } from '../../../lib/forms/useZodForm'
import { grantFormSchema, type GrantFormValues } from './grantFormSchema'
import type { Grant, GrantUpsert } from '../types'

interface Props {
  visible: boolean
  initial: Grant | null
  submitting: boolean
  nameError?: string | null
  codeError?: string | null
  onClearErrors: () => void
  onSubmit: (payload: GrantUpsert) => void
  onHide: () => void
}

/** Yetki ekle/düzenle formu (020: react-hook-form + zod). nameTr/code/object zorunlu. */
export default function GrantFormDialog({
  visible,
  initial,
  submitting,
  nameError,
  codeError,
  onClearErrors,
  onSubmit,
  onHide,
}: Readonly<Props>) {
  const { t } = useTranslation()
  const { control, handleSubmit, reset, setError } = useZodForm(grantFormSchema, {
    defaultValues: { nameTr: '', nameEn: '', code: '', object: '' },
  })

  useEffect(() => {
    if (visible)
      reset({
        nameTr: initial?.nameTr ?? '',
        nameEn: initial?.nameEn ?? '',
        code: initial?.code ?? '',
        object: initial?.object ?? '',
      })
  }, [visible, initial, reset])

  // Sunucu alan hataları (DUPLICATE_*) forma; kaynağı hemen temizle (aynı hata tekrar tetiklenebilsin).
  useEffect(() => {
    if (nameError) setError('nameTr', { type: 'server', message: nameError })
    if (codeError) setError('code', { type: 'server', message: codeError })
    if (nameError || codeError) onClearErrors()
  }, [nameError, codeError, setError, onClearErrors])

  const onValid = (v: GrantFormValues) =>
    onSubmit({
      nameTr: v.nameTr,
      nameEn: v.nameEn ? v.nameEn : null,
      code: v.code,
      object: v.object,
    })

  return (
    <FormDialog
      visible={visible}
      header={initial ? t('admin.grants.form.editTitle') : t('admin.grants.form.newTitle')}
      submitLabel={t('admin.grants.form.save')}
      submitting={submitting}
      submitDisabled={submitting}
      onSubmit={handleSubmit(onValid)}
      onHide={onHide}
    >
      <div className="admin-form">
        <FormTextField control={control} name="nameTr" label={t('admin.grants.form.nameTr')} required autoFocus />
        <FormTextField control={control} name="nameEn" label={t('admin.grants.form.nameEn')} />
        <FormTextField control={control} name="code" label={t('admin.grants.form.code')} required />
        <FormTextField control={control} name="object" label={t('admin.grants.form.object')} required />
      </div>
    </FormDialog>
  )
}
